import { defineStore } from 'pinia'
import { ref } from 'vue'
import { createStoreBase } from '@/presentation/stores/resources/storeBase'
import DependencyContainer from '@/presentation/configuration/DependencyContainer'
import { FetchAllLearningModulesUseCase } from '@/core/useCases/learning/module/FetchAllLearningModulesUseCase'
import { FetchLearningModuleUseCase } from '@/core/useCases/learning/module/FetchLearningModuleUseCase'
import { CreateLearningModuleUseCase } from '@/core/useCases/learning/module/CreateLearningModuleUseCase'
import { UpdateLearningModuleUseCase } from '@/core/useCases/learning/module/UpdateLearningModuleUseCase'
import { UpdateLearningModuleStatusUseCase } from '@/core/useCases/learning/module/UpdateLearningModuleStatusUseCase'
import {
  type LearningModule,
  LearningModuleInput
} from '@/core/domain/learning/module/LearningModule'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus'
import { NabooError } from '@/core/infrastructure/services/NabooError'
import { useRouter } from 'vue-router'
import { storeEventBus } from '@/presentation/stores/utils/storeEventBus.ts'
import type { ILearningModuleUrlOptions } from '@/core/domain/learning/module/ILearningModuleUrlOptions.ts'
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'

export const useLearningModulesStore = defineStore('learningModules', () => {
  // Dépendances externes
  const router = useRouter()

  // Cas d'utilisation
  const fetchAllLearningModulesUseCase = DependencyContainer.resolve(FetchAllLearningModulesUseCase)
  const fetchLearningModuleUseCase = DependencyContainer.resolve(FetchLearningModuleUseCase)
  const createLearningModuleUseCase = DependencyContainer.resolve(CreateLearningModuleUseCase)
  const updateLearningModuleUseCase = DependencyContainer.resolve(UpdateLearningModuleUseCase)
  const updateLearningModuleStatusUseCase = DependencyContainer.resolve(
    UpdateLearningModuleStatusUseCase
  )

  // Événements
  storeEventBus.useEventListener('learning:grain:deleted', (payload) => {
    deleteGrainFromModule(payload)
  })

  storeEventBus.useEventListener('learning:quizgrain:updated', (payload) => {
    updateQuizInModules(payload.quiz)
  })

  // Base commune du store
  const storeBase = createStoreBase([
    'fetchAllModules',
    'fetchModuleById',
    'createModule',
    'updateModule',
    'updateModuleStatus'
  ])

  // État
  const modulesList = ref<LearningModule[]>([])

  // Fonctions helper
  function updateState(module: LearningModule) {
    const index = modulesList.value.findIndex((m) => m.id === module.id)
    if (index !== -1) {
      modulesList.value[index] = module
    } else {
      modulesList.value.push(module)
    }
    storeBase.updateLastUpdated()
  }

  function handleError(error: unknown, message: string) {
    let nabooError: NabooError

    if (!(error instanceof NabooError)) {
      nabooError = new NabooError({
        message,
        status: 500,
        name: 'ModuleStoreError',
        code: 'MODULE_STORE_ERROR',
        originalError: error instanceof Error ? error : undefined
      })
    } else {
      nabooError = error
    }

    storeBase.setError(nabooError)
    storeBase.showErrorToast('Erreur', nabooError.message)
    return Promise.reject(error)
  }

  // Actions
  async function fetchAllModules(courseId: number, options?: ILearningModuleUrlOptions) {
    try {
      storeBase.loading.value.fetchAllModules = true
      storeBase.resetError()

      const modules = await fetchAllLearningModulesUseCase.execute(courseId, options)
      modulesList.value = modules
      storeBase.updateLastUpdated()

      return modules
    } catch (error) {
      return handleError(error, 'Erreur lors de la récupération des modules')
    } finally {
      storeBase.loading.value.fetchAllModules = false
    }
  }

  async function fetchModuleById(
    moduleId: number,
    courseId: number,
    options?: ILearningModuleUrlOptions
  ) {
    try {
      storeBase.loading.value.fetchModuleById = true
      storeBase.resetError()

      const module = await fetchLearningModuleUseCase.execute(courseId, moduleId, options)
      updateState(module)

      return module
    } catch (error) {
      return handleError(error, 'Erreur lors de la récupération du module')
    } finally {
      storeBase.loading.value.fetchModuleById = false
    }
  }

  async function createModule(module: LearningModuleInput, courseId: number) {
    try {
      storeBase.loading.value.createModule = true
      storeBase.resetError()

      const newModule = await createLearningModuleUseCase.execute(courseId, module)
      updateState(newModule)

      await router.push({
        name: 'learning-course-update-module',
        params: { courseId: courseId, moduleId: newModule.id }
      })

      storeBase.showSuccessToast('Module créé', `Le module ${module.title} a été créé avec succès`)
      return newModule
    } catch (error) {
      return handleError(error, 'Erreur lors de la création du module')
    } finally {
      storeBase.loading.value.createModule = false
    }
  }

  async function updateModule(module: LearningModuleInput, moduleId: number, courseId: number) {
    try {
      storeBase.loading.value.updateModule = true
      storeBase.resetError()

      const updatedModule = await updateLearningModuleUseCase.execute(courseId, moduleId, module)
      updateState(updatedModule)

      storeEventBus.emit('learning:module:updated', {
        moduleId: updatedModule.id,
        courseId: courseId,
        module: updatedModule
      })

      storeBase.showSuccessToast('Succès', 'Le module a été mis à jour avec succès')
      return updatedModule
    } catch (error) {
      return handleError(error, 'Erreur lors de la mise à jour du module')
    } finally {
      storeBase.loading.value.updateModule = false
    }
  }

  async function updateModuleStatus(
    status: LearningStatus.Values,
    moduleId: number,
    courseId: number
  ) {
    try {
      storeBase.loading.value.updateModuleStatus = true
      storeBase.resetError()

      const module = getModuleById(moduleId)
      if (!module) {
        throw new NabooError({
          message: "Le module n'a pas été trouvé",
          status: 404,
          name: 'ModuleNotFoundError',
          code: 'MODULE_NOT_FOUND'
        })
      }

      if (module.activeGrains().length === 0 && status === LearningStatus.Values.ACTIVE) {
        throw new NabooError({
          message: 'Le module doit avoir au moins un grain actif pour être activé',
          status: 400,
          name: 'InvalidStatusError',
          code: 'MODULE_NO_ACTIVE_GRAINS'
        })
      }

      if (
        module.status === LearningStatus.Values.ACTIVE &&
        status === LearningStatus.Values.ACTIVE
      ) {
        throw new NabooError({
          message: 'Le module est déjà actif',
          status: 400,
          name: 'InvalidStatusError',
          code: 'MODULE_ALREADY_ACTIVE'
        })
      }

      const updatedModule = await updateLearningModuleStatusUseCase.execute(
        courseId,
        moduleId,
        status
      )

      updateState(updatedModule)

      storeEventBus.emit('learning:module:updated', {
        moduleId: updatedModule.id,
        courseId: courseId,
        module: updatedModule
      })

      storeBase.showSuccessToast('Succès', 'Le statut du module a été mis à jour avec succès')
      return updatedModule
    } catch (error) {
      return handleError(error, 'Erreur lors de la mise à jour du statut du module')
    } finally {
      storeBase.loading.value.updateModuleStatus = false
    }
  }

  function deleteGrainFromModule(payload: { grainId: number; moduleId: number }) {
    const module = modulesList.value.find((m) => m.id === payload.moduleId)

    if (module)
      module.learningGrains = module.learningGrains.filter((g) => g.id !== payload.grainId)
  }

  function updateQuizInModules(updatedQuiz: Quiz) {
    console.log('🔥 updateQuizInModules called with:', updatedQuiz)
    console.log('Current modulesList:', modulesList.value)

    let quizUpdated = false

    // Parcourir tous les modules pour trouver le quiz à mettre à jour
    modulesList.value.forEach((module) => {
      console.log(
        'Checking module:',
        module.id,
        'with',
        module.learningGrains?.length || 0,
        'grains'
      )

      module.learningGrains.forEach((grain) => {
        console.log(
          'Checking grain:',
          grain.id,
          'with quizPre:',
          grain.quizPre?.id,
          'quizPost:',
          grain.quizPost?.id
        )

        // Mettre à jour le quiz pré si c'est le bon
        if (grain.quizPre && grain.quizPre.id === updatedQuiz.id) {
          console.log('✅ Updating quizPre for grain:', grain.id)
          grain.quizPre = updatedQuiz
          quizUpdated = true
        }

        // Mettre à jour le quiz post si c'est le bon
        if (grain.quizPost && grain.quizPost.id === updatedQuiz.id) {
          console.log('✅ Updating quizPost for grain:', grain.id)
          grain.quizPost = updatedQuiz
          quizUpdated = true
        }
      })
    })

    if (quizUpdated) {
      console.log('✅ Quiz updated in modules')
      // Mettre à jour le timestamp de dernière mise à jour
      storeBase.updateLastUpdated()
    } else {
      console.log('⚠️ No matching grain found for quiz update')
    }
  }

  // Getters
  function getModuleById(moduleId: number) {
    return modulesList.value.find((module) => module.id === moduleId)
  }

  // Reset
  function reset() {
    modulesList.value = []
    storeBase.resetBase()
  }

  return {
    // État
    modulesList,
    ...storeBase,

    // Actions
    fetchAllModules,
    fetchModuleById,
    createModule,
    updateModule,
    updateModuleStatus,
    reset,

    // Getters
    getModuleById
  }
})
